import React from 'react';
import { useViewModel } from './useViewModel';
import '../GwpCard/gwp-card.module.css';
import styles from './gwp-helper-modal.module.css';

/**
 * PersonalisationHelperModal component displays a modal dialog for helper information in the personalisation flow.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback for closing the modal
 * @param {string} [props.title] - Title for the modal
 * @param {React.ReactNode} [props.children] - Modal content
 * @param {string} [props.footerLabelLeft] - Left footer label
 * @param {string} [props.footerLabelRight] - Right footer label
 * @param {boolean} [props.noContentPaddingBottom] - Whether to remove padding bottom from the content
 * @returns {import('react').JSX.Element}
 */
export function GwpHelperModal({
  isOpen,
  onClose,
  title,
  children,
  footerLabelLeft,
  footerLabelRight,
  noContentPaddingBottom = false
}) {
  const { dialogRef, handleClose } = useViewModel({ isOpen, onClose });

  return (
    <dialog ref={dialogRef} className={styles['personalisation__helper-modal']}>
      <div className={styles['personalisation__helper-modal-header']}>
        {title && <h2 className="heading-2xl">{title}</h2>}
        <button
          className={styles['personalisation__helper-modal-close'] + ' svg-wrapper button-reset'}
          onClick={handleClose}
          aria-label="Close"
        >
          <span aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M5.35404 4.64632C5.15878 4.45106 4.8422 4.45106 4.64694 4.64632C4.45167 4.84159 4.45167 5.15817 4.64693 5.35343L11.2934 11.9999L4.64693 18.6463C4.45167 18.8416 4.45167 19.1582 4.64694 19.3535C4.8422 19.5487 5.15878 19.5487 5.35404 19.3535L12.0005 12.707L18.6469 19.3535C18.8422 19.5487 19.1588 19.5487 19.3541 19.3535C19.5493 19.1582 19.5493 18.8416 19.3541 18.6463L12.7076 11.9999L19.3541 5.35343C19.5493 5.15817 19.5493 4.84159 19.3541 4.64632C19.1588 4.45106 18.8422 4.45106 18.6469 4.64632L12.0005 11.2928L5.35404 4.64632Z"
                fill="#171717"
              />
            </svg>
          </span>
        </button>
      </div>
      <div
        className={`${styles['personalisation__helper-modal-content']} ${noContentPaddingBottom ? styles['personalisation__helper-modal-content--no-pb'] : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
      <div className={styles['personalisation__helper-modal-footer']}>
        {(footerLabelLeft || footerLabelRight) && (
          <div className={styles['personalisation__helper-modal-footer-detail'] + ' body-sm'}>
            {footerLabelLeft && (
              <p className={styles['personalisation__helper-modal-footer-detail-label']}>{footerLabelLeft}</p>
            )}
            {footerLabelRight && (
              <p className={styles['personalisation__helper-modal-footer-detail-price']}>{footerLabelRight}</p>
            )}
            <button
              className={styles['personalisation__helper-modal-footer-detail-control'] + ' button-reset'}
              onClick={handleClose}
            >
              <span className="svg-wrapper">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.70554 7.20541C9.86825 7.04269 10.1321 7.04269 10.2948 7.20541L15.2948 12.2054C15.4575 12.3681 15.4575 12.632 15.2948 12.7947C15.1321 12.9574 14.8683 12.9574 14.7055 12.7947L10.0002 8.0893L5.29479 12.7947C5.13207 12.9574 4.86825 12.9574 4.70553 12.7947C4.54282 12.632 4.54282 12.3681 4.70553 12.2054L9.70554 7.20541Z"
                    fill="#171717"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}
        <button
          className="button button--secondary button--black button--full-width"
          onClick={handleClose}
          aria-label="Close"
        >
          Close
        </button>
      </div>
    </dialog>
  );
}
